import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { IconButton, Input, Text, Button } from '../ui';
import { Check, Edit, X, Plus, MapPin } from 'lucide-react';
import useMediaQuery from '../../hooks/useMediaQuery';
import useLogoBackground from '../../hooks/useLogoBackground';

const SummaryForm = ({ initialData, onSave, isLoading }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [locationInput, setLocationInput] = useState('');
  const { isMobile, isSmallMobile } = useMediaQuery();
  const isMobileView = isMobile || isSmallMobile;
  const logoBackground = useLogoBackground(initialData?.logo);
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: {
      user_id: initialData?.user_id || '',
      profile_id: initialData?.profile_id || '',
      business_name: initialData?.business_name || '',
      logo: initialData?.logo || '',
      industry: initialData?.industry || '',
      country: initialData?.country || '',
      city: initialData?.city || '',
      description: initialData?.description || '',
      audience: initialData?.audience || '',
      selling_points: initialData?.selling_points || [''],
      call_to_actions: initialData?.call_to_actions || [''],
      core_values: initialData?.core_values || [''],
      voice_tone: initialData?.voice_tone || '',
    },
  });

  // Update form when initialData changes
  useEffect(() => {
    if (initialData) {
      reset({
        user_id: initialData?.user_id || '',
        profile_id: initialData?.profile_id || '',
        business_name: initialData?.business_name || '',
        logo: initialData?.logo || '',
        industry: initialData?.industry || '',
        country: initialData?.country || '',
        city: initialData?.city || '',
        description: initialData?.description || '',
        audience: initialData?.audience || '',
        selling_points: initialData?.selling_points || [''],
        call_to_actions: initialData?.call_to_actions || [''],
        core_values: initialData?.core_values || [''],
        voice_tone: initialData?.voice_tone || '',
      });

      // Set combined location input
      const city = initialData?.city || '';
      const country = initialData?.country || '';
      setLocationInput(country ? `${city ? city + ', ' : ''}${country}` : city);
    }
  }, [initialData, reset]);

  const formValues = watch();

  const {
    fields: sellingPointsFields,
    append: appendSellingPoint,
    remove: removeSellingPoint,
  } = useFieldArray({
    control,
    name: 'selling_points',
  });

  const {
    fields: ctaFields,
    append: appendCta,
    remove: removeCta,
  } = useFieldArray({
    control,
    name: 'call_to_actions',
  });

  const {
    fields: coreValuesFields,
    append: appendCoreValue,
    remove: removeCoreValue,
  } = useFieldArray({
    control,
    name: 'core_values',
  });

  const onSubmit = (data) => {
    // Split location input into city and country
    const locationParts = locationInput.split(',').map((part) => part.trim());
    const city = locationParts[0] || '';
    const country = locationParts[1] || '';

    // Extract only the fields needed for the API, excluding user_id and profile_id
    const { user_id, profile_id, industry, ...summaryData } = data;

    // Update with parsed location
    summaryData.city = city;
    summaryData.country = country;

    onSave(summaryData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    reset();
    setIsEditing(false);
  };

  return (
    <div
      className={
        'tour-summary-section rounded-lg shadow-sm p-2 md:p-6 h-full flex flex-col'
      }
      style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)' }}
    >
      <div className='flex items-start justify-between flex-shrink-0 mb-4'>
        <div>
          <Text variant='title' className={`${isMobileView ? 'text-base' : ''}`} style={{ color: 'var(--text-primary)' }}>
            Business Summary
          </Text>
          <Text
            variant='text-secondary'
            className={`mt-1 ${isMobileView ? 'text-xs' : ''}`}
            style={{ color: 'var(--text-muted)' }}
          >
            Review and edit your business information
          </Text>
        </div>
        <div className='flex items-center gap-2'>
          {isEditing ? (
            <>
              <IconButton
                icon={Check}
                tooltip='Save changes'
                onClick={handleSubmit(onSubmit)}
                variant='primary'
                disabled={!isDirty || isLoading}
              />
              <IconButton
                icon={X}
                tooltip='Cancel editing'
                onClick={handleCancel}
                variant='default'
              />
            </>
          ) : (
            <IconButton
              icon={Edit}
              tooltip='Edit summary'
              onClick={() => setIsEditing(true)}
              variant='ghostPrimary'
              className='tour-edit-summary-button'
            />
          )}
        </div>
      </div>

      <div
        className='space-y-6 overflow-y-auto pr-2 flex-1 scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent hover:scrollbar-thumb-slate-300'
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <form onSubmit={handleSubmit(onSubmit)} className='pr-2'>
          {/* Hidden fields */}
          <input type='hidden' {...register('user_id')} />
          <input type='hidden' {...register('profile_id')} />

          {/* Section 1: Logo, Business Name, Location */}
          <div className='rounded-lg p-4 gap-4' style={{ border: '1px solid var(--border)', backgroundColor: 'var(--bg-surface)' }}>
            <div>
              <div className='flex items-center gap-6'>
                {/* Logo */}
                <div className='flex-shrink-0'>
                  {formValues.logo && (
                    <img
                      src={formValues.logo}
                      alt='Business logo'
                    className={`h-16 w-16 object-contain p-2 rounded flex items-center justify-center`}
                    style={{
                      border: '1px solid var(--border)',
                      backgroundColor: logoBackground === 'dark' ? 'var(--bg-deep)' : 'var(--bg-card)',
                    }}
                      onError={(e) => {
                        e.target.onError = null;
                        e.target.src = 'https://placehold.co/600x400/png?text=No+Logo';
                      }}
                    />
                  )}
                </div>

                {/* Business Name and Location */}
                <div className='flex-1 space-y-3'>
                  {/* Business Name */}
                  <div>
                    {isEditing ? (
                      <Controller
                        name='business_name'
                        control={control}
                        rules={{ required: 'Business name is required' }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            type='text'
                            placeholder='Business Name'
                            variant='ghost-sm'
                            error={errors.business_name?.message}
                          />
                        )}
                      />
                    ) : (
                      <Text
                        as='h2'
                        variant='title'
                        className={`font-bold ${isMobileView ? 'text-base' : ''}`}
                        style={{ color: 'var(--text-primary)' }}
                      >
                        {formValues.business_name}
                      </Text>
                    )}
                  </div>

                  {/* Location */}
                  <div>
                    {isEditing ? (
                      <div>
                        <Input
                          type='text'
                          value={locationInput}
                          onChange={(e) => setLocationInput(e.target.value)}
                          placeholder='City, Country'
                          icon={MapPin}
                          iconPosition='left'
                          variant='ghost-sm'
                          onIconClick={() => {
                            // Future: Open map modal
                            console.log('Open map');
                          }}
                        />
                        <input
                          type='hidden'
                          {...register('city')}
                          value={locationInput.split(',')[0]?.trim() || ''}
                        />
                        <input
                          type='hidden'
                          {...register('country')}
                          value={locationInput.split(',')[1]?.trim() || ''}
                        />
                      </div>
                    ) : (
                      <div className='flex items-center gap-1 text-xs' style={{ color: 'var(--text-muted)' }}>
                        <MapPin className='w-4 h-4' style={{ color: 'var(--text-muted)' }} />
                        <span className='truncate'>
                          {formValues.city && `${formValues.city}, `}
                          {formValues.country}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {isEditing && (
              <div className='mt-3'>
                <Controller
                  name='logo'
                  control={control}
                  rules={{
                    pattern: {
                      value: /^https?:\/\/.+/,
                      message: 'Please enter a valid URL',
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type='url'
                      placeholder='Logo URL'
                      variant='ghost-sm'
                      error={errors.logo?.message}
                    />
                  )}
                />
              </div>
            )}
          </div>

          <div className='mt-6 space-y-6 mb-4'>
            {/* Section 2: Description */}
            <div>
              <Text
                as='h3'
                variant='text-secondary'
                className={`text-sm font-bold mb-2 ${isMobileView ? 'text-xs' : ''}`}
                style={{ color: 'var(--text-primary)' }}
              >
                Description
              </Text>
              {isEditing ? (
                <Controller
                  name='description'
                  control={control}
                  rules={{ required: 'Description is required' }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type='textarea'
                      variant='ghost-sm'
                      rows={4}
                      error={errors.description?.message}
                    />
                  )}
                />
              ) : (
                <div className='p-3 rounded border text-sm' style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
                  {formValues.description}
                </div>
              )}
            </div>

            {/* Section 3: Target Audience */}
            <div>
              <Text
                as='h3'
                variant='text-secondary'
                className={`text-sm font-bold mb-2 ${isMobileView ? 'text-xs' : ''}`}
                style={{ color: 'var(--text-primary)' }}
              >
                Target Audience
              </Text>
              {isEditing ? (
                <Controller
                  name='audience'
                  control={control}
                  rules={{ required: 'Target audience is required' }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type='textarea'
                      variant='ghost-sm'
                      rows={3}
                      error={errors.audience?.message}
                    />
                  )}
                />
              ) : (
                <div className='p-3 rounded border text-sm' style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
                  {formValues.audience}
                </div>
              )}
            </div>

            {/* Section 4: Voice & Tone */}
            <div>
              <Text
                as='h3'
                variant='text-secondary'
                className={`text-sm font-bold mb-2 ${isMobileView ? 'text-xs' : ''}`}
                style={{ color: 'var(--text-primary)' }}
              >
                Voice & Tone
              </Text>
              {isEditing ? (
                <Controller
                  name='voice_tone'
                  control={control}
                  rules={{ required: 'Voice and tone is required' }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type='textarea'
                      variant='ghost-sm'
                      rows={3}
                      error={errors.voice_tone?.message}
                    />
                  )}
                />
              ) : (
                <div className='p-3 rounded border text-sm' style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
                  {formValues.voice_tone}
                </div>
              )}
            </div>
          </div>

          {/* Section 5: Selling Points */}
          <div className='rounded-lg shadow-sm p-6 mb-4' style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)' }}>
            <Text
              as='h3'
              variant='text-secondary'
              className={`text-sm font-bold mb-4 ${isMobileView ? 'text-xs' : ''}`}
              style={{ color: 'var(--text-primary)' }}
            >
              Selling Points
            </Text>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
              {sellingPointsFields.map((field, index) => (
                <div key={field.id} className='w-full'>
                  {isEditing ? (
                    <div className='flex items-center gap-2 p-2.5 rounded' style={{ backgroundColor: 'var(--bg-card-alt)', border: '1px solid var(--border-subtle)' }}>
                      <Controller
                        name={`selling_points.${index}`}
                        control={control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            type='text'
                            variant='pillSquare'
                            placeholder='Enter point'
                            className='text-xs placeholder:opacity-60'
                            style={{ color: 'var(--text-body)' }}
                            icon={() => (
                              <X
                                className='w-4 h-4 hover:text-red-500 flex-shrink-0 cursor-pointer'
                                onClick={() => removeSellingPoint(index)}
                              />
                            )}
                            iconPosition='right'
                          />
                        )}
                      />
                    </div>
                  ) : (
                    <div className='p-2.5 rounded text-xs w-full' style={{ backgroundColor: 'var(--bg-card-alt)', border: '1px solid var(--border-subtle)', color: 'var(--text-body)' }}>
                      {formValues.selling_points[index]}
                    </div>
                  )}
                </div>
              ))}
              {isEditing && (
                <Button
                  type='button'
                  onClick={() => appendSellingPoint('')}
                  className='inline-flex items-center justify-center gap-1 p-2.5 rounded text-xs transition-colors'
                  style={{ backgroundColor: 'var(--bg-card-alt)', border: '1px solid var(--border-subtle)', color: 'var(--text-body)' }}
                  icon={() => <Plus className='w-3 h-3' />}
                  iconPosition='left'
                >
                  Add Point
                </Button>
              )}
            </div>
          </div>

          {/* Section 6: Call to Actions */}
          <div className='rounded-lg shadow-sm p-6 mb-4' style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)' }}>
            <Text
              as='h3'
              variant='text-secondary'
              className={`text-sm font-bold mb-4 ${isMobileView ? 'text-xs' : ''}`}
              style={{ color: 'var(--text-primary)' }}
            >
              Call to Actions
            </Text>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
              {ctaFields.map((field, index) => (
                <div key={field.id} className='w-full'>
                  {isEditing ? (
                    <div className='flex items-center gap-2 p-2.5 rounded' style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
                      <Controller
                        name={`call_to_actions.${index}`}
                        control={control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            type='text'
                            variant='pillSquare'
                            placeholder='Enter CTA'
                            className='text-xs w-full placeholder:opacity-60'
                            style={{ color: 'var(--text-body)' }}
                            icon={() => (
                              <X
                                className='w-4 h-4 hover:text-red-500 flex-shrink-0 cursor-pointer'
                                onClick={() => removeSellingPoint(index)}
                              />
                            )}
                            iconPosition='right'
                          />
                        )}
                      />
                    </div>
                  ) : (
                    <div
                      className={`p-2.5 rounded text-xs w-full truncate ${isMobileView ? 'text-xs' : ''}`}
                      style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text-body)' }}
                    >
                      {formValues.call_to_actions[index]}
                    </div>
                  )}
                </div>
              ))}
              {isEditing && (
                <Button
                  type='button'
                  onClick={() => appendCta('')}
                  variant='center'
                  className='inline-flex items-center justify-center gap-1 p-2.5 rounded text-xs transition-colors'
                  style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text-body)' }}
                  icon={() => <Plus className='w-3 h-3' />}
                  iconPosition='left'
                >
                  Add CTA
                </Button>
              )}
            </div>
          </div>

          {/* Section 7: Core Values */}
          <div className='rounded-lg shadow-sm p-6' style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)' }}>
            <Text
              as='h3'
              variant='text-secondary'
              className={`text-sm font-bold mb-4 ${isMobileView ? 'text-xs' : ''}`}
              style={{ color: 'var(--text-primary)' }}
            >
              Core Values
            </Text>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
              {coreValuesFields.map((field, index) => (
                <div key={field.id} className='w-full'>
                  {isEditing ? (
                    <div className='flex items-center gap-2 p-2.5 rounded' style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
                      <Controller
                        name={`core_values.${index}`}
                        control={control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            type='text'
                            variant='pillSquare'
                            placeholder='Enter value'
                            className='text-xs placeholder:opacity-50'
                            style={{ color: 'var(--text-body)' }}
                          />
                        )}
                      />
                      <IconButton
                        icon={() => <X className='w-3 h-3' />}
                        onClick={() => removeCoreValue(index)}
                        className='hover:text-red-500 flex-shrink-0'
                        variant='ghostPrimary'
                        size='sm'
                      />
                    </div>
                  ) : (
                    <div className='p-2.5 rounded text-xs w-full' style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
                      {formValues.core_values[index]}
                    </div>
                  )}
                </div>
              ))}
              {isEditing && (
                <Button
                  type='button'
                  onClick={() => appendCoreValue('')}
                  className='inline-flex items-center justify-center gap-1 p-2.5 rounded text-xs transition-colors'
                  style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}
                  icon={() => <Plus className='w-3 h-3' />}
                  iconPosition='left'
                >
                  Add Value
                </Button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SummaryForm;
